/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: pages\CrmCallbackPage.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Integrations Config module.
----------------------------------------------------------------*/

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useI18n } from '../../../hooks/useI18n';
import axios from 'axios';

// A simple loading screen while processing the callback
export const CrmCallbackPage = () => {
    const { provider, token } = useParams<{ provider: string; token: string }>();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { showSuccess, showError } = useNotifications();
    const { t } = useI18n();
    const [status, setStatus] = useState('Processing...');

    useEffect(() => {
        const processCallback = async () => {
            const code = searchParams.get('code');
            const error = searchParams.get('error');

            if (error) {
                showError(`OAuth Error: ${error}`);
                navigate('/settings/integrations');
                return;
            }

            if (!code) {
                showError('No code returned from provider');
                navigate('/settings/integrations');
                return;
            }

            try {
                // We need to retrieve the original config (clientId, etc.) to complete the exchange.
                // However, security-wise, sending clientId/Secret again from frontend is risky if not stored in session?
                // But "Bring Your Own App" model implies the frontend/user owns these keys.
                // Strategy: We stored the 'config' in localStorage before redirecting?
                // OR we just send the code to backend, and backend retrieves the PARTIAL connection from DB?
                // BUT we haven't saved the connection yet, we are in "Connect" phase.

                // REVISED FLOW:
                // 1. User saves Config in `CrmConfigurationConfig` -> Validated & Saved to DB (with status 'pending' or just settings).
                // 2. User clicks Connect -> Redirects.
                // 3. Callback -> Backend uses stored settings to exchange code.

                // Check if we have the config in localStorage to send along with code (stateless approach)
                const storedConfigStart = localStorage.getItem(`crm_pending_${provider}`);
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                let payload: any = {
                    provider,
                    code,
                    redirectUri: `${window.location.origin}/crm/callback/${provider}/${token}`,
                };

                if (storedConfigStart) {
                    const config = JSON.parse(storedConfigStart);
                    payload = { ...payload, ...config };
                    // Cleanup
                    localStorage.removeItem(`crm_pending_${provider}`);
                }

                // Call Backend
                await axios.post('/api/integrations/connect', payload);

                showSuccess(t('crm.connectionSuccess', 'Connected successfully'));
                navigate('/settings/integrations');

            } catch (err) {
                console.error(err);
                showError(t('crm.connectionError', 'Connection failed'));
                navigate(`/settings/integrations/${provider}`);
            }
        };

        if (provider) {
            processCallback();
        }
    }, [provider, searchParams, navigate, showSuccess, showError, token, t]);

    return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-background text-foreground">
            <h2 className="text-xl font-semibold mb-4">{t('crm.connecting', 'Connecting to CRM...')}</h2>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <p className="mt-4 text-muted-foreground">{status}</p>
        </div>
    );
};
